import React from 'react';
import ReactDOM from 'react-dom';
import { node, oneOfType, string, bool, number, arrayOf, func } from 'prop-types';
import { on, off } from '../../utils/reactwow/event';
import splice from '../../utils/reactwow/splice';
import trim from '../../utils/reactwow/trim';
import scrollParent from '../../utils/reactwow/scrollParent';
import PropTypes from 'prop-types';

const LISTEN_FLAG = 'data-react-wow';
const defaultBoundingClientRect = { top: 0, right: 0, bottom: 0, left: 0, width: 0, height: 0 };

interface CacheType {
  listeners: ReactWOW[];
  pending: ReactWOW[];
}

const caches: CacheType = {
  listeners: [],
  pending: [],
};

let passiveEventSupported = false;

try {
  const opts = Object.defineProperty({}, 'passive', {
    get: function() {
      passiveEventSupported = true;
      return true;
    }
  });
  window.addEventListener('testPassive', () => {}, opts);
  window.removeEventListener('testPassive', () => {}, opts);
} catch (e) {}

const passiveEvent = passiveEventSupported ? { capture: false, passive: true } : false;

const checkOverflowVisible = (component: ReactWOW, parent: Element): boolean => {
  const node = ReactDOM.findDOMNode(component) as Element;

  let parentTop, parentHeight;

  try {
    ({ top: parentTop, height: parentHeight } = parent.getBoundingClientRect());
  } catch (e) {
    ({ top: parentTop, height: parentHeight } = defaultBoundingClientRect);
  }

  const windowInnerHeight = window.innerHeight || document.documentElement.clientHeight;

  const intersectionTop = Math.max(parentTop, 0);
  const intersectionHeight = Math.min(windowInnerHeight, parentTop + parentHeight) - intersectionTop;

  let top, height;

  try {
    ({ top, height } = node.getBoundingClientRect());
  } catch (e) {
    ({ top, height } = defaultBoundingClientRect);
  }

  const offsetTop = top - intersectionTop;
  
    // Ensure offsets are properly defined
    const offsets = Array.isArray(component.props.offset)
        ? component.props.offset
        : [component.props.offset ?? 0, component.props.offset ?? 0];

  return (offsetTop - offsets[0] <= intersectionHeight) && (offsetTop + height + offsets[1] >= 0);
};

const checkNormalVisible = (component: ReactWOW): boolean => {
  const node = ReactDOM.findDOMNode(component) as Element;

  if (!(node.clientWidth || node.clientHeight || node.getClientRects().length)) return false;

  let top, elementHeight;

  try {
    ({ top, height: elementHeight } = node.getBoundingClientRect());
  } catch (e) {
    ({ top, height: elementHeight } = defaultBoundingClientRect);
  }

  const windowInnerHeight = window.innerHeight || document.documentElement.clientHeight;
  const offsets = Array.isArray(component.props.offset)
  ? component.props.offset
  : [component.props.offset ?? 0, component.props.offset ?? 0];

  return (top - offsets[0] <= windowInnerHeight) && (top + elementHeight + offsets[1] >= 0);
};

const checkVisible = (component: ReactWOW): void => {
  const node = ReactDOM.findDOMNode(component) as Element;
  if (!node) return;

  const parent = scrollParent(node);
  const isOverflow = component.props.overflow && parent !== document.documentElement;
  const visible = isOverflow ? checkOverflowVisible(component, parent) : checkNormalVisible(component);

  if (visible && !component.visible) {
    caches.pending.push(component);
    component.visible = true;
    component.setState({ stopped: false });
    component.forceUpdate();
  }
};

const purgePending = (): void => {
  caches.pending.forEach((component) => splice(caches.listeners, component));
  caches.pending = [];
};

const scrollHandler = (): void => {
  caches.listeners.forEach((component) => checkVisible(component));
  purgePending();
};

interface ReactWOWProps {
  duration?: string;
  delay?: string;
  iteration?: string;
  animation?: string;
  children?: React.ReactNode;
  scroll?: boolean;
  resize?: boolean;
  animateClass?: string;
  offset?: number | number[];
  overflow?: boolean;
  disabled?: boolean;
  callback?: (node: Element | Text | null) => void;
}

interface ReactWOWState {
  stopped: boolean;
}

class ReactWOW extends React.Component<ReactWOWProps, ReactWOWState> {
    static defaultProps: Partial<ReactWOWProps> = {
        duration: '',
        delay: '',
        iteration: '',
        animation: '',
        scroll: true,
        resize: true,
        animateClass: 'animated',
        offset: 0,
        overflow: false,
        disabled: false,
        callback: () => {},
      };
    
      visible = false;
    
      constructor(props: ReactWOWProps) {
        super(props);
        this.state = {
          stopped: false,
        };
      }

      static propTypes = {
        duration: PropTypes.string,
        delay: PropTypes.string,
        iteration: PropTypes.string,
        animation: PropTypes.string,
        children: PropTypes.node,
        scroll: PropTypes.bool,
        resize: PropTypes.bool,
        animateClass: PropTypes.string,
        offset: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
        overflow: PropTypes.bool,
        callback: PropTypes.func,
      };

  componentDidMount() {
    if (this.props.disabled) return;

    if (this.props.overflow) {
      const parent = scrollParent(ReactDOM.findDOMNode(this) as Element);
      if (parent && typeof parent.getAttribute === 'function') {
        const listenerCount = 1 + (+Number(parent.getAttribute(LISTEN_FLAG) ?? 0));
        if (listenerCount === 1) {
          on(parent, 'scroll', scrollHandler, passiveEvent);
        }
        parent.setAttribute(LISTEN_FLAG, listenerCount.toString());
      }
    } else if (!caches.listeners.length) {
      if (this.props.scroll) {
        on(window, 'scroll', scrollHandler, passiveEvent);
      }
      if (this.props.resize) {
        on(window, 'resize', scrollHandler, passiveEvent);
      }
    }

    caches.listeners.push(this);
    checkVisible(this);
  }

  shouldComponentUpdate() {
    return this.visible;
  }

  componentWillUnmount() {
    if (this.props.disabled) return;

    if (this.props.overflow) {
      const parent = scrollParent(ReactDOM.findDOMNode(this) as Element);
      if (parent && typeof parent.getAttribute === 'function') {
        const listenerCount = (+Number(parent.getAttribute(LISTEN_FLAG) ?? 0)) - 1;
        if (listenerCount === 0) {
          off(parent, 'scroll', scrollHandler, passiveEvent);
          parent.removeAttribute(LISTEN_FLAG);
        } else {
          parent.setAttribute(LISTEN_FLAG, listenerCount.toString());
        }
      }
    }

    splice(caches.listeners, this);

    if (!caches.listeners.length) {
      off(window, 'scroll', scrollHandler, passiveEvent);
      off(window, 'resize', scrollHandler, passiveEvent);
    }
  }

  customStyle = (hidden: boolean): React.CSSProperties => {
    const { duration, delay, iteration, animation, disabled } = this.props;
    const style: React.CSSProperties = {
      animationName: hidden ? 'none' : animation,
      visibility: hidden && !disabled ? 'hidden' : 'visible',
    };

    if (duration) style.animationDuration = duration;
    if (delay) style.animationDelay = delay;
    if (iteration) style.animationIterationCount = iteration;

    return style;
  };

  resetAnimation = (e: React.AnimationEvent<Element>): void => {
    if (e.type.toLowerCase().indexOf('animationend') !== -1) {
      this.setState({ stopped: true }, () => {
        const { callback } = this.props;
        if (typeof callback === 'function') {
          callback(ReactDOM.findDOMNode(this));
        }
      });
    }
  };

  mergeProps(props: React.HTMLProps<Element>) {
    const { animation, animateClass } = this.props;
    const style = this.customStyle(!this.visible);
    const className = this.visible ? `${animation} ${!this.state.stopped ? animateClass : ''}` : animation;

    return {
      ...props,
      style: {
        ...props.style,
        ...style,
      },
      className: trim(`${props.className ? props.className : ''} ${className}`),
      onAnimationEnd: this.resetAnimation,
    };
  }

  render() {
    const { children, disabled } = this.props;

    if (disabled) {
      return children || null;
    }

    return React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, this.mergeProps(child.props));
      }
      return child;
    });
  }
}

ReactWOW.propTypes = {
  duration: string,
  delay: string,
  iteration: string,
  animation: string,
  children: node,
  scroll: bool,
  resize: bool,
  animateClass: string,
  offset: oneOfType([number, arrayOf(number)]),
  overflow: bool,
  callback: func,
};



ReactWOW.defaultProps = {
  duration: '',
  delay: '',
  iteration: '',
  animation: '',
  scroll: true,
  resize: true,
  animateClass: 'animated',
  offset: 0,
  overflow: false,
  disabled: false,
  callback: () => {},
};

export default ReactWOW;
